import { datadogRum } from "@datadog/browser-rum";
import { IdTokenResult } from "@firebase/auth-types";
import {
  KikErrorMessages,
  KikGenericResponse,
  KikGenericResponseBase,
  KikHttpStatusCodes,
  KikMarketingCloudDoubleOptInProcedures,
  KikResponseMessageTypes,
  KikRestVerbs,
  KikSocialSignInKeys,
  KikSocialSignInUxModes,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import {
  KikCustomerLoginFields,
  KikCustomerPasswordUpdateFields,
  KikCustomerSignUpWithActionCodeFields,
  KikEmailUpdateWithActionCodeFields,
  KikMarketingCloudSendActionCodeEmailParams,
  KikPasswordResetWithActionCodeFields,
  KikProspectSendEmailVerificationFields,
  KikProspectSignUpWithActionCodeFields,
  KikSocialCustomerFields,
  KikUnsubscribeNewsletterParams,
  KikUserZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { AuthenticationOpenID } from "@springtree/eva-services-authentication-openid";
import { Core } from "@springtree/eva-services-core";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState } from "recoil";

import { useCart } from "~/hooks/use-cart";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import Eva from "~/services/eva";
import Firebase from "~/services/firebase";
import { HttpConfig, HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { StorageFactory } from "~/services/storage.factory";
import { ApiConstants } from "~/shared/api/api-constants.class";
import { ApiUtils } from "~/shared/api/api-utils.class";
import { SendEmailVerificationRequest } from "~/shared/api/auth/send-email-verification/send-email-verification.model";
import { ErrorResponse } from "~/shared/api/error-response.class";
import { CacheKeys } from "~/shared/cache-keys";
import { EvaUserAccountType } from "~/shared/eva/eva-user-account-type.enum";
import { LoginSignUpOrigins } from "~/shared/user/login-sign-up-origins.enum";
import AppRoutes from "~/utils/app-routes";
import CommonUtils from "~/utils/common-utils";
import invariant from "~/utils/invariant";
import TrackingUtils from "~/utils/tracking-utils";

type AuthCallConfigBody =
  | KikEmailUpdateWithActionCodeFields
  | KikMarketingCloudSendActionCodeEmailParams
  | KikPasswordResetWithActionCodeFields
  | KikProspectSendEmailVerificationFields
  | KikProspectSignUpWithActionCodeFields
  | KikUnsubscribeNewsletterParams;

type AuthCallConfig = Partial<Pick<HttpConfig<AuthCallConfigBody>, "body" | "method" | "queryParams" | "headers">> & {
  addAuth?: boolean;
};

interface EvaHookHttpError extends Error {
  response: Response;
}

const useAuth = (initialStates?: { isLoading?: boolean }) => {
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(!0);
  const [isLoading, setIsLoading] = useState(!!initialStates?.isLoading);
  const [firebaseToken, setFirebaseToken] = useState<string | void>();
  const [firebaseIdTokenResult, setFirebaseIdTokenResult] = useState<IdTokenResult | void>();
  const [authError, setAuthError] = useState<ErrorResponse | null>(null);
  const [providerId, setProviderId] = useState<number>(-1);

  const unsubscribeAuthObs = useRef<() => void>();

  const [currentUserToken, setCurrentUserToken] = useRecoilState(state.currentUser.currentUserTokenState);
  const [socialProvider, setSocialProvider] = useState<KikSocialSignInKeys | null>();
  const cart = useCart()?.ShoppingCart;
  const user = hooks.useGetServiceResponse(state.currentUser.currentUserState)?.User;
  const { newErrorGlobalNotification, newWarningGlobalNotification } = useGlobalNotification();
  const router = useRouter();
  //
  const sessionStorageFactory = useMemo(() => new StorageFactory<CacheKeys>("sessionStorage"), []);

  const onLoginEvaError = (error: Error) => {
    throw error;
  };

  // EVA HOOKS - START
  const evaCartModCreateCustomer = hooks.useShoppingCartModify({
    service: Core.CreateCustomer,
  });
  const evaCartModLogin = hooks.useShoppingCartModify({
    service: Core.Login,
    options: {
      onError: onLoginEvaError,
    },
  });
  const evaServiceLogin = hooks.useCallService({
    service: Core.Login,
  });
  const evaServiceLogout = hooks.useCallService({
    service: Core.Logout,
  });
  const evaServiceOpenIdConfigs = hooks.useCallService({
    service: AuthenticationOpenID.GetAvailableOpenIDConfigurations,
  });
  const refreshUserData = hooks.useSetServiceStale({ serviceState: state.currentUser.currentUserState });
  const updateUser = hooks.useCallService({ service: Core.UpdateUser });
  // EVA HOOKS - END

  // PRIVATE

  const _checkRedirectResult = () => {
    Logger.instance.debug("use-auth: checkRedirectResult requested");
    setIsLoading(!0);
    Firebase.instance
      .getRedirectResult()
      .finally(() => setIsLoading(!1))
      .then(function (result) {
        if (result && result.providerId) {
          const provider = Firebase.instance.providersRelation[result.providerId];
          _checkSocialUser(provider);
        }
      })
      .catch((error) => {
        Logger.instance.error(`use-auth: getRedirectResult errored`, error);
        newErrorGlobalNotification(
          {
            title: "generic.error",
            description: ApiUtils.errorFactory(error).messages![0].message || KikErrorMessages.GENERIC_ERROR,
          },
          { timeout: 7500 }
        );
      });
  };

  const _checkSocialUser = async (socialKey: KikSocialSignInKeys) => {
    setIsLoading(!0);
    const firebaseUser = Firebase.instance.loggedUser;
    Logger.instance.info("use-auth: social login success", firebaseUser);
    const t = await firebaseUser?.getIdToken();
    // Update the firebaseToken state
    setFirebaseToken(() => t);
    try {
      await _doCall(ApiConstants.endpointsV2.authSocialCustomer, {
        headers: {
          Authorization: `Bearer ${t}`,
        },
      });
      // IF WE GOT A USER AND IT'S A CUSTOMER ON EVA BASICALLY WE CAN LOG IN, OTHERWISE IT COULD BE A REGISTRATION/CONVERSION
      setSocialProvider(socialKey);
    } catch (err: any) {
      // 404 means I can register, not a real error, so I need to clear up the authError
      if (err?.status === KikHttpStatusCodes.NotFound) {
        setAuthError(null);
        const redirectUrl = new URL([`/${router.locale}`, AppRoutes.SIGN_UP].join(""), location.href);
        redirectUrl.searchParams.set("social", socialKey);

        router.push(redirectUrl.href, redirectUrl.pathname);
      }
    }
  };

  const _doCall = async <T = void>(
    url: string,
    { body, queryParams = {}, method, addAuth, headers = {} }: AuthCallConfig
  ): Promise<T> => {
    setIsLoading(!0);
    setAuthError(null);
    try {
      return await HttpService.$http<T>({
        url,
        method: method || (body ? KikRestVerbs.POST : KikRestVerbs.GET),
        body,
        queryParams,
        headers: {
          ...(addAuth ? getFirebaseTokenAuthHeader() : {}),
          ...headers,
        },
      });
    } catch (e: any) {
      Logger.instance.error("use-auth: call errored", {
        endpoint: url,
        error: e,
      });
      setAuthError(e.body || new ErrorResponse());
      // We rethrow to avoid having if-else outside
      return Promise.reject(e);
    } finally {
      setIsLoading(!1);
    }
  };

  const _orchestrateLoginMethod = async (idToken: string): Promise<EVA.Core.LoginResponse | void> => {
    let innerProviderId = providerId;
    if (innerProviderId < 0) {
      innerProviderId = await _retrieveOpenIdProviderId();
      setProviderId(innerProviderId);
    }
    const loginPayload = Eva.buildSSOLoginPayload(idToken, innerProviderId);
    try {
      Logger.instance.debug("use-auth: orchestrated login cart", cart);
      const loginResp =
        cart?.ID && !cart?.Customer?.ID
          ? await evaCartModLogin({
              payload: loginPayload,
            })
          : await evaServiceLogin(
              loginPayload,
              { authenticationToken: "" },
              {
                onError: onLoginEvaError,
              }
            );

      if (loginResp?.Authentication === 2 && loginResp.User) {
        const token = loginResp.User.AuthenticationToken;
        _signalRCleanUp();
        setCurrentUserToken(token);
      }
    } catch (e) {
      Logger.instance.error("use-auth: orchestrated login failure", e);
      let response: { FailureReason?: number } | void = void 0;
      try {
        response = await (e as EvaHookHttpError).response.json();
      } catch (e) {}
      Logger.instance.warn("Handling EVA error", {
        response,
      });
      setAuthError(
        ErrorResponse.create({
          messages: KikErrorMessages.GENERIC_ERROR,
          errorCode: response?.FailureReason,
        })
      );
      return Promise.reject(e);
    }
  };

  const _retrieveOpenIdProviderId = async (): Promise<number> => {
    const configs = await evaServiceOpenIdConfigs(undefined, { authenticationToken: "" }).then(
      (r) => r?.Providers || []
    );
    const config = configs.find((c) => c.Name === process.env.NEXT_PUBLIC_OPEN_ID_PROVIDER_NAME);

    return config?.ID || -1;
  };

  const _signalRCleanUp = () => {
    sessionStorageFactory.remove(CacheKeys.SIGNAL_R_TOKEN);
  };

  const _trackingEvents: Partial<Record<KikMarketingCloudDoubleOptInProcedures | "_default", string>> = {
    [KikMarketingCloudDoubleOptInProcedures.SIGN_UP_CUSTOMER]: "",
  };

  const _verifyIdToken = () => {
    return Firebase.instance
      .verifyIdToken()
      .catch((e) => Logger.instance.warn("use-auth: idToken verification failed", e));
  };

  // PUBLIC

  /**
   * Perform a login on Eva using the existing Firebase token
   */
  const autoLogin = async (): Promise<ErrorResponse | void> => {
    Logger.instance.debug("use-auth: calling autoLogin");
    setIsLoading(!0);
    setAuthError(null);
    setSocialProvider(null);
    try {
      // Extract the id_token from the returned authentication object
      const firebaseIdToken = await Firebase.instance.loggedUser?.getIdToken();
      setFirebaseToken(firebaseIdToken); // Need to force this otherwise the asynchronicity breaks with redirects
      await _orchestrateLoginMethod(firebaseIdToken!);
    } catch (err: any) {
      Logger.instance.error("use-auth: login errored", err);
      setAuthError(ApiUtils.errorFactory(err));
      return Promise.reject();
    } finally {
      setIsLoading(!1);
    }
  };

  const createGuestCustomer = async (order: EVA.Core.OrderDto) => {
    invariant(!!order.BillingAddress, `Order is being used to create customer but no address is defined.`);

    let user: EVA.Core.CreateCustomer.Customer = {
      DateOfBirth: KikUserZodSchemas.guestBirthdayDto.toISODate()!,
      FirstName: order.BillingAddress.FirstName,
      LastName: order.BillingAddress.LastName,
      EmailAddress: order.BillingAddress.EmailAddress,
      BillingAddress: order.BillingAddress,
    };

    if (order.ShippingAddress) {
      user.ShippingAddress = order.ShippingAddress;
    }

    const customer = await evaCartModCreateCustomer({
      payload: {
        OrderID: order.ID,
        AutoLogin: true,
        AccountType: EvaUserAccountType.Incognito,
        AttachToOrder: true,
        User: user,
      },
    });

    if (customer?.User.AuthenticationToken) {
      _signalRCleanUp();
      setCurrentUserToken(customer.User.AuthenticationToken);
    }

    return customer;
  };

  const emailUpdateCompletion = (data: KikEmailUpdateWithActionCodeFields) => {
    return _doCall(ApiConstants.endpointsV2.authEmailUpdate, {
      addAuth: !0,
      method: KikRestVerbs.POST,
      body: data,
    }).then(() => logout());
  };

  const getFirebaseTokenAuthHeader = (): { Authorization: string } | {} => {
    return firebaseToken ? { Authorization: `Bearer ${firebaseToken}` } : {};
  };

  const joinKikoMeCompletion = (params: KikUnsubscribeNewsletterParams): Promise<void> => {
    return _doCall(ApiConstants.endpointsV2.authLoyalty, {
      method: KikRestVerbs.POST,
      queryParams: params,
    });
  };

  const login = async (loginFields: KikCustomerLoginFields): Promise<ErrorResponse | void> => {
    Logger.instance.info(`use-auth: login requested with email ${loginFields.EmailAddress}`);
    setIsLoading(!0);
    setAuthError(null);
    try {
      await Firebase.instance.setPersistence(
        CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_FIREBASE_FORCE_PERSISTENCE) || !!loginFields.RememberMe
      );
      const user = await Firebase.instance.signInWithEmailAndPassword(loginFields.EmailAddress, loginFields.password);

      // Extract the id_token from the returned authentication object
      const firebaseIdToken = await user?.getIdToken();
      setFirebaseToken(firebaseIdToken); // Need to force this otherwise the asynchronicity breaks with redirects
      await _orchestrateLoginMethod(firebaseIdToken!);
    } catch (err: any) {
      Logger.instance.error(`use-auth: login errored for email ${loginFields.EmailAddress}`, err);
      setAuthError(ApiUtils.errorFactory(err));
    } finally {
      setIsLoading(!1);
    }
  };

  const loginAsCustomer = async (
    loginFields: Omit<KikCustomerLoginFields, "RememberMe">
  ): Promise<ErrorResponse | void> => {
    try {
      Logger.instance.warn(`use-auth: loginAsCustomer requested with email ${loginFields.EmailAddress}`);
      await logout().catch((e) => Logger.instance.warn("use-auth: couldn't logout", e));
      const customToken = await _doCall<
        KikGenericResponseBase<{
          token: string;
        }>
      >(ApiConstants.endpointsV2.authLoginAsCustomer, {
        body: loginFields,
      }).then((r) => r.data?.token!);
      const token = await Firebase.instance
        .signInWithCustomToken(customToken)
        .then((credential) => credential?.user.getIdToken());
      _orchestrateLoginMethod(token);
    } catch (err: any) {
      Logger.instance.error("use-auth: login errored", err);
    }
  };

  const logout = async (): Promise<ErrorResponse | void> => {
    setIsLoading(!0);
    setAuthError(null);
    try {
      // Sign out from Google
      await Firebase.instance.signOut().catch((e) => Logger.instance.warn("use-auth: Firebase logout failed", e));
      // Sign out from EVA
      await evaServiceLogout().catch((e) => Logger.instance.warn("use-auth: EVA logout failed", e));
      _signalRCleanUp();
    } catch (err: any) {
      Logger.instance.error("use-auth: logout errored", err);
      setAuthError(new ErrorResponse());
    } finally {
      setIsLoading(!1);
    }
  };

  const passwordChange = (params: KikCustomerPasswordUpdateFields) => {
    setIsLoading(!0);
    setAuthError(null);

    return Firebase.instance
      .updatePassword(params.oldPassword, params.password)
      .finally(() => setIsLoading(!1))
      .then(() => logout())
      .catch((err) => {
        Logger.instance.error("use-auth: updatePassword errored", err);
        setAuthError(ApiUtils.errorFactory(err));
        // We rethrow to avoid having if-else outside
        return Promise.reject();
      });
  };

  const passwordResetWithActionCode = (params: KikPasswordResetWithActionCodeFields) => {
    setIsLoading(!0);
    setAuthError(null);

    return _doCall(ApiConstants.endpointsV2.authPasswordReset, {
      body: params,
    });
  };

  const sendEmailVerification = (request: SendEmailVerificationRequest): Promise<void> => {
    Logger.instance.info(`use-auth: send email verification ${request.procedure || "NO_PROCEDURE"}`, request);

    return _doCall(ApiConstants.endpointsV2.authSendEmailVerification, {
      body: request,
    })
      .then((r) => {
        // We only track sign-up (customer or prospect) request, since this method is used for all double opt-ins
        const event = _trackingEvents[request.procedure || "_default"];
        event && TrackingUtils.track(TrackingUtils.mapUserEvent(event, { method: "email" }));
        return r;
      })
      .catch((error) => {
        Logger.instance.error("use-auth: sendEmailVerification errored", {
          request,
          error,
        });
        return Promise.reject(error);
      });
  };

  const signalRToSessionStorage = (token: string): void => {
    return sessionStorageFactory.set(CacheKeys.SIGNAL_R_TOKEN, token);
  };

  const signInWithSocialProvider = async (socialKey: KikSocialSignInKeys): Promise<void> => {
    setIsLoading(!0);
    setAuthError(null);
    setSocialProvider(null);
    Logger.instance.info("use-auth: social login requested");
    try {
      await Firebase.instance.signInWithSocialProvider(socialKey);
      // Extract the id_token from the returned authentication object
    } catch (err: any) {
      Logger.instance.warn("use-auth: signInWithSocialProvider errored", err);
      setIsLoading(() => !1);
      // Firebase errors need to be put into authError
      setAuthError(ApiUtils.errorFactory(err));

      return Promise.reject();
    }

    _checkSocialUser(socialKey);
  };

  const signUpCustomerWithActionCode = (
    signUpCustomerWithActionCodeFields: KikCustomerSignUpWithActionCodeFields,
    origin?: LoginSignUpOrigins
  ) => {
    Logger.instance.info(
      `use-auth: signUpCustomerWithActionCode requested with email ${signUpCustomerWithActionCodeFields.EmailAddress}`
    );
    return _doCall<KikGenericResponse<void>>(ApiConstants.endpointsV2.authCustomer, {
      body: signUpCustomerWithActionCodeFields,
      queryParams: {
        origin: origin || "",
      },
    }).then((r) => {
      TrackingUtils.track(TrackingUtils.mapUserEvent("sign_up", { method: "email" }));
      const firstWarning = r.messages?.find((m) => m.type === KikResponseMessageTypes.WARNING);
      if (firstWarning) {
        newWarningGlobalNotification(
          {
            title: "generic.signUpWarning",
            description: `ERROR.${firstWarning.message}`, // PUSH_SUBSCRIPTIONS_ERROR
          },
          { timeout: 7500 }
        );
      }
      return r;
    });
  };

  const signUpProspectWithActionCode = (prospectSignUpFields: KikProspectSignUpWithActionCodeFields): Promise<void> => {
    return _doCall(ApiConstants.endpointsV2.authProspect, {
      body: prospectSignUpFields,
    })
      .then(() => {
        TrackingUtils.track(
          TrackingUtils.mapUserEvent("generate_lead", { lead_type: "newsletter", lead_result: "submit_ok" })
        );
        return Promise.resolve();
      })
      .catch(() => {
        TrackingUtils.track(
          TrackingUtils.mapUserEvent("generate_lead", { lead_type: "newsletter", lead_result: "submit_ko" })
        );
        return Promise.reject();
      });
  };

  const signUpSocial = async (
    socialCustomer: KikSocialCustomerFields,
    socialKey: KikSocialSignInKeys
  ): Promise<void> => {
    return _doCall(ApiConstants.endpointsV2.authSocialCustomer, {
      body: socialCustomer,
    }).then((r) => {
      TrackingUtils.track(TrackingUtils.mapUserEvent("sign_up_requested", { method: socialKey }));
      return r;
    });
  };

  const unsubscribeNewsletterCompletion = (
    unsubscribeNewsletterParams: KikUnsubscribeNewsletterParams
  ): Promise<void> => {
    return _doCall(ApiConstants.endpointsV2.authProspect, {
      method: KikRestVerbs.DELETE,
      queryParams: unsubscribeNewsletterParams,
    });
  };

  /**
   * Updates a guest customer with data coming from an order.
   * For example, if a guest user performs two orders, we must update the attributes (e.g. email, name...)
   * This updates the customer retroactively, so old orders will display the new data.
   */
  const updateGuestCustomer = async (order: EVA.Core.OrderDto) => {
    invariant(order.CustomerID, `Order is being used to update customer, but no customer is associated.`);
    invariant(order.BillingAddress, `Order is being used to update customer, but has no address.`);

    await updateUser({
      ID: order.CustomerID,
      FirstName: order.BillingAddress.FirstName,
      LastName: order.BillingAddress.LastName,
      EmailAddress: order.BillingAddress.EmailAddress,
    });
  };

  const updateUserProfile = (firstName: string, lastName: string) => {
    setIsLoading(!0);
    setAuthError(null);

    return Firebase.instance
      .updateUserProfile({
        displayName: `${firstName} ${lastName}`,
      })
      .catch((err) => {
        Logger.instance.error("use-auth: updateUserProfile errored", err);
        setAuthError(ApiUtils.errorFactory(err));
        // We rethrow to avoid having if-else outside
        return Promise.reject();
      })
      .finally(() => setIsLoading(!1));
  };

  const hasEvaAuth = !!currentUserToken && !!user?.ID; // Do not consider anonymous users as authenticated

  useEffect(() => {
    if (Firebase.instance.socialLoginUXMode && !isLoading) {
      Logger.instance.warn("use-auth: useEffect checkRedirectResult", Firebase.instance.socialLoginUXMode);
      Firebase.instance.socialLoginUXMode !== KikSocialSignInUxModes.POPUP && _checkRedirectResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Firebase.instance.socialLoginUXMode]);

  useEffect(() => {
    unsubscribeAuthObs.current?.(); // Turn off previous listeners
    unsubscribeAuthObs.current = Firebase.instance.addAuthStateChangedCallback(async (firebaseUser) => {
      let newToken: string | void = void 0;
      let newIdTokenResult: IdTokenResult | void = void 0;
      // A new user session is started
      if (firebaseUser) {
        newToken = await firebaseUser.getIdToken();
        newIdTokenResult = await _verifyIdToken();
        if (user) {
          if (user.AccountType !== EvaUserAccountType.Standard) {
            Logger.instance.warn(
              `use-auth: Firebase token with non standard user (${user.EmailAddress})! Performing Firebase logout!`
            );
            await Firebase.instance.signOut(); // Logout from Firebase only
          } else if (user.EmailAddress !== firebaseUser.email) {
            Logger.instance.warn("use-auth: Firebase/EVA user mismatch! Performing logout!", {
              userEmail: user.EmailAddress,
              firebaseEmail: firebaseUser.email,
            });
            logout();
          }
        }
      } else {
        const signalR = sessionStorageFactory.get(CacheKeys.SIGNAL_R_TOKEN);
        if (user) {
          // So basically if user is customer, but we don't have a signalR, or there is a signalR, but it doesn't match the user token, then cleanup
          if (
            (!signalR && user.AccountType !== EvaUserAccountType.Incognito) ||
            (signalR && signalR !== currentUserToken)
          ) {
            Logger.instance.warn(
              "use-auth: found customer EVA user but no firebase session and no signalR, cleaning up!",
              {
                userEmail: user.EmailAddress,
              }
            );
            _signalRCleanUp(); // void custom SignalR backup
            setCurrentUserToken(""); // void EVA session
          }
        }
      }
      setFirebaseToken(newToken);
      setFirebaseIdTokenResult(newIdTokenResult);
      setIsLoadingUser(!1);
    });

    return () => {
      unsubscribeAuthObs.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserToken, user, firebaseToken]);

  useEffect(() => {
    if (socialProvider && !isLoading) {
      Logger.instance.info(`SocialSignIn: autologin can be performed for ${socialProvider}!`);
      autoLogin().then(() => {
        Logger.instance.debug(`SocialSignIn: autologin complete with ${socialProvider}!`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialProvider, isLoading]);

  useEffect(() => {
    const evaId = user?.ID?.toString();
    const backendId = user?.BackendID;
    const sessionAttrs = {
      cart: cart?.ID,
      id: evaId,
      backendId: evaId === backendId ? void 0 : backendId, // Identify legacy users
      email: user?.EmailAddress,
      name: `${user?.FirstName} ${user?.LastName}`,
      userAccountType: ["STANDARD", "GUEST"][user?.AccountType || 3],
    };
    /**
     * Since instance already exists, this won't do much besides modifying the config
     * updating (overwriting) the tags every time cart id or user id changes.
     * This will also make tags available everywhere a log (default instance) is fired
     */
    Logger.init({
      customTags: sessionAttrs,
    });
    datadogRum.setUser(sessionAttrs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.ID, user?.ID]);

  return {
    // Values
    authError,
    currentUserToken,
    enabledSocials: Firebase.instance.enabledSocials,
    firebaseToken,
    firebaseIdTokenResult,
    firebaseUser: Firebase.instance.loggedUser,
    hasEvaAuth,
    isAuthenticated: !!firebaseToken && hasEvaAuth && user.AccountType === EvaUserAccountType.Standard,
    isLoading,
    isLoadingUser,
    isMigrated: new RegExp(process.env.NEXT_PUBLIC_CID_PATTERN!).test(user?.BackendID || ""),
    socialProvider,
    user,
    // Methods
    autoLogin,
    createGuestCustomer,
    emailUpdateCompletion,
    joinKikoMeCompletion,
    login,
    loginAsCustomer,
    logout,
    passwordChange,
    passwordResetWithActionCode,
    refreshUserData,
    sendEmailVerification,
    signalRToSessionStorage,
    signInWithSocialProvider,
    signUpCustomerWithActionCode,
    signUpProspectWithActionCode,
    signUpSocial,
    unsubscribeNewsletterCompletion,
    updateUserProfile,
    updateGuestCustomer,
  };
};

export default useAuth;
